export const OPERATING_SYSTEM_RUNTIME_STORAGE_MESSAGE = "applyt:runtime-storage";

export type OperatingSystemRuntimeStorageName = "localStorage" | "sessionStorage";

export type OperatingSystemRuntimeStorageSeed = Record<
  OperatingSystemRuntimeStorageName,
  Record<string, string>
>;

type RuntimeStorageEnvelope = {
  version: 1;
  updatedAt: number;
  values: Record<string, string>;
};

const RUNTIME_STORAGE_VERSION = 1;
const RUNTIME_STORAGE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_STORAGE_ENTRIES = 300;
const MAX_STORAGE_KEY_LENGTH = 256;
const MAX_STORAGE_VALUE_LENGTH = 128 * 1024;
const MAX_STORAGE_TOTAL_LENGTH = 256 * 1024;
const STORAGE_KEY_PREFIX = "applyt:os-runtime-storage:v1";
const EXCLUDED_KEYS = new Set(["base44_access_token", "token"]);

function emptySeed(): OperatingSystemRuntimeStorageSeed {
  return { localStorage: {}, sessionStorage: {} };
}

function storageKey(operatingSystemId: string, name: OperatingSystemRuntimeStorageName): string {
  return `${STORAGE_KEY_PREFIX}:${operatingSystemId}:${name}`;
}

function browserStorage(name: OperatingSystemRuntimeStorageName): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return name === "localStorage" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

export function sanitizeOperatingSystemRuntimeStorageValues(
  value: unknown,
): Record<string, string> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const output: Record<string, string> = {};
  let totalLength = 0;
  for (const [key, item] of Object.entries(value)) {
    if (Object.keys(output).length >= MAX_STORAGE_ENTRIES) break;
    if (EXCLUDED_KEYS.has(key) || !key || key.length > MAX_STORAGE_KEY_LENGTH) continue;
    if (typeof item !== "string" || item.length > MAX_STORAGE_VALUE_LENGTH) continue;
    totalLength += key.length + item.length;
    if (totalLength > MAX_STORAGE_TOTAL_LENGTH) return null;
    output[key] = item;
  }
  return output;
}

function readStorageValues(
  operatingSystemId: string,
  name: OperatingSystemRuntimeStorageName,
): Record<string, string> {
  const storage = browserStorage(name);
  if (!storage) return {};
  try {
    const parsed = JSON.parse(storage.getItem(storageKey(operatingSystemId, name)) || "null") as Partial<RuntimeStorageEnvelope> | null;
    if (
      parsed?.version !== RUNTIME_STORAGE_VERSION
      || typeof parsed.updatedAt !== "number"
      || Date.now() - parsed.updatedAt > RUNTIME_STORAGE_TTL_MS
    ) {
      return {};
    }
    return sanitizeOperatingSystemRuntimeStorageValues(parsed.values) ?? {};
  } catch {
    return {};
  }
}

export function readOperatingSystemRuntimeStorageSeed(
  operatingSystemId: string,
): OperatingSystemRuntimeStorageSeed {
  if (!operatingSystemId) return emptySeed();
  return {
    localStorage: readStorageValues(operatingSystemId, "localStorage"),
    sessionStorage: readStorageValues(operatingSystemId, "sessionStorage"),
  };
}

export function persistOperatingSystemRuntimeStorage(
  operatingSystemId: string,
  name: OperatingSystemRuntimeStorageName,
  values: unknown,
): boolean {
  if (!operatingSystemId) return false;
  const sanitized = sanitizeOperatingSystemRuntimeStorageValues(values);
  const storage = browserStorage(name);
  if (!sanitized || !storage) return false;
  const envelope: RuntimeStorageEnvelope = {
    version: RUNTIME_STORAGE_VERSION,
    updatedAt: Date.now(),
    values: sanitized,
  };
  try {
    storage.setItem(storageKey(operatingSystemId, name), JSON.stringify(envelope));
    return true;
  } catch {
    return false;
  }
}

export function parseOperatingSystemRuntimeStorageMessage(data: unknown): {
  name: OperatingSystemRuntimeStorageName;
  values: Record<string, string>;
} | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const record = data as { type?: unknown; name?: unknown; values?: unknown };
  if (record.type !== OPERATING_SYSTEM_RUNTIME_STORAGE_MESSAGE) return null;
  if (record.name !== "localStorage" && record.name !== "sessionStorage") return null;
  const values = sanitizeOperatingSystemRuntimeStorageValues(record.values);
  return values ? { name: record.name, values } : null;
}

export function injectOperatingSystemRuntimeStorageSeed(
  html: string,
  seed: OperatingSystemRuntimeStorageSeed,
): string {
  const serialized = JSON.stringify(seed)
    .replace(/&/g, "\\u0026")
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
  const seedScript = `<script data-applyt-storage-seed="true">window.__APPLYT_RUNTIME_STORAGE_SEED__=${serialized};</script>`;
  const shimPattern = /<script\b[^>]*data-applyt-storage-shim=(?:"true"|'true')[^>]*>/i;
  if (shimPattern.test(html)) return html.replace(shimPattern, `${seedScript}$&`);
  if (/<head\b[^>]*>/i.test(html)) {
    return html.replace(/<head\b([^>]*)>/i, `<head$1>${seedScript}`);
  }
  return `${seedScript}${html}`;
}
